import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { type SurveyGroupFilterInformation_MainQuery as Data } from '~/gql/graphql'
import {
  ensureExists,
  exists,
  filterExists,
  findByPath,
  pathAssume,
  pathOr,
} from '~/utils'

import { useMainQueryKey } from './useMainQueryKey'

export const useMainQueryData = () => {
  const { t } = useTranslation()
  const queryKey = useMainQueryKey()

  const { data } = useQuery<Data>({ enabled: false, queryKey })

  return React.useMemo(() => {
    const surveyGroup = data?.surveyGroup

    if (!exists(surveyGroup)) {
      return null
    }

    return R.pipe(surveyGroup, (surveyGroup) => ({
      ...surveyGroup,
      brands: R.pipe(surveyGroup.brands, filterExists),
      customerAttributeGroups: R.pipe(
        surveyGroup.customerAttributeGroups,
        filterExists,
        R.map((customerAttributeGroup) => {
          const pickedCustomerAttributeGroup = R.pick(customerAttributeGroup, [
            'id',
            'name',
          ])

          const customerAttributes = R.pipe(
            customerAttributeGroup.customerAttributes,
            filterExists,
          )

          if (
            customerAttributes.every((customerAttribute) =>
              exists(customerAttribute.id),
            )
          ) {
            return {
              ...pickedCustomerAttributeGroup,
              customerAttributes: customerAttributes.map(
                (customerAttribute) => ({
                  idLike: ensureExists(
                    customerAttribute.id,
                    t('assertion_failed'),
                  ),
                  name: customerAttribute.name,
                }),
              ),
            }
          }

          const surveys = R.pipe(
            surveyGroup.surveys,
            filterExists,
            R.map((survey) => ({
              ...survey,
              customerAttributeGroups: R.pipe(
                survey.customerAttributeGroups,
                filterExists,
                R.map((customerAttributeGroup) => ({
                  ...customerAttributeGroup,
                  customerAttributes: R.pipe(
                    customerAttributeGroup.customerAttributes,
                    filterExists,
                    R.map((customerAttribute, index) => ({
                      ...customerAttribute,
                      idLike: `${index + 1}`,
                    })),
                  ),
                })),
              ),
            })),
          )
          const customerAttributeGroups = surveys.map((survey) => {
            const customerAttributeGroupId = R.pipe(
              customerAttributeGroup.attachedCustomerAttributeGroups ?? [],
              findByPath('surveyId', survey.id),
              pathAssume('customerAttributeGroupId'),
            )

            return R.pipe(
              surveys,
              findByPath('id', survey.id),
              pathOr('customerAttributeGroups', []),
              findByPath('id', customerAttributeGroupId),
            )
          })

          const customerAttributeNames =
            customerAttributeGroups[0]?.customerAttributes.map(
              R.prop('name'),
            ) ?? []
          const hasSameCustomerAttributeNames = customerAttributeGroups.every(
            (customerAttributeGroup) =>
              exists(customerAttributeGroup) &&
              R.isDeepEqual(
                customerAttributeNames,
                customerAttributeGroup.customerAttributes.map(R.prop('name')),
              ),
          )
          if (hasSameCustomerAttributeNames) {
            return {
              ...pickedCustomerAttributeGroup,
              customerAttributes: customerAttributeNames.map((name, index) => ({
                idLike: `${index + 1}`,
                name,
              })),
            }
          }

          return {
            ...pickedCustomerAttributeGroup,
            customerAttributes: [],
          }
        }),
      ),
      questions: R.pipe(
        surveyGroup.questions,
        filterExists,
        R.map((question) => ({
          ...question,
          answerOptions: R.pipe(question.answerOptions, filterExists),
        })),
      ),
    }))
  }, [data, t])
}
